'use client';

import { c as _c } from "react/compiler-runtime";
import { formatCents, parseCents, toLowerCase } from 'betterplace-utils';
import classNames from 'classnames';
import { AmountField, Button, Heading, Icon, LayoutCluster, LayoutModal, LayoutStack, getButtonStyles, LinkButton, Keys } from 'design-system/client';
import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import { useCallback, useMemo } from 'react';
import styles from './CodonationSelection.module.css';
import { type ModalContentProps, type PredefinedAmountBoxProps } from './types';
import CodonationCalculator from '@/donationForm/_dependencies/CodonationCalculator';
import ZeroCodonationMessage, { ZeroCodonationMessageContextProvider } from '@/donationPages/_dependencies/components/formParts/ZeroCodonationMessage';
import { useCodonationValue, useDonationFormConfig, useDonationFormValues } from '@/donationPages/_dependencies/helpers';
import useCodonationModalState from '@/donationPages/_dependencies/helpers/useCodonationModalState';
import { postMessageScrollTop } from '@/helpers/utils';
const images = ['https://betterplace-assets.betterplace.org/assets/donations/codonation/slider/level-3-1e395bb471ac74d84b464698d679d7f786c51da262e3cb5ffb7cc2ada7c45193.png', 'https://betterplace-assets.betterplace.org/assets/donations/codonation/slider/level-5-1ded43de393f6436c062886bf2ee7ae4e5b419286adb4de4d6157ff83aefa453.png', 'https://betterplace-assets.betterplace.org/assets/donations/codonation/slider/level-7-7d86dc933b3fbfdbe829c3c1ea92033d201578227d412c9893f409755e6a5605.png'] as const;
function ModalContent(t0) {
  const $ = _c(73);
  const {
    handleClose
  } = t0;
  const locale = useLocale();
  const t = useTranslations("nextjs.donate.codonation_selection.codonation_modal");
  const {
    wording,
    carrierName,
    carrierProfilePicture
  } = useDonationFormConfig();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["amount_cents"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [amountCents] = useDonationFormValues(t1);
  const [value, setValue, commitValue] = useCodonationValue();
  let t2;
  if ($[1] !== amountCents) {
    t2 = CodonationCalculator.prefills(amountCents);
    $[1] = amountCents;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const prefills = t2;
  let t3;
  if ($[3] !== commitValue || $[4] !== handleClose) {
    t3 = () => {
      commitValue();
      handleClose();
    };
    $[3] = commitValue;
    $[4] = handleClose;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const storeAndClose = t3;
  let t4;
  if ($[6] !== setValue || $[7] !== storeAndClose) {
    t4 = cents => {
      setValue(cents);
      storeAndClose();
    };
    $[6] = setValue;
    $[7] = storeAndClose;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handlePredefinedAmountSelection = t4;
  let t5;
  if ($[9] !== storeAndClose) {
    t5 = event => {
      if (event.key !== Keys.Enter) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      storeAndClose();
    };
    $[9] = storeAndClose;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleEnter = t5;
  let t6;
  if ($[11] !== setValue) {
    t6 = t7 => {
      const {
        target
      } = t7;
      setValue(parseCents(target.value));
    };
    $[11] = setValue;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const onValueChanged = t6;
  let t7;
  if ($[13] !== wording) {
    t7 = toLowerCase(wording);
    $[13] = wording;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = `${t7}.platform_introduction`;
  let t9;
  if ($[15] !== t || $[16] !== t8) {
    t9 = t(t8);
    $[15] = t;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = <p className={styles.introduction}>{t9}</p>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== wording) {
    t11 = toLowerCase(wording);
    $[20] = wording;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const t12 = `${t11}.call_to_action`;
  let t13;
  if ($[22] !== t || $[23] !== t12) {
    t13 = t(t12);
    $[22] = t;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13) {
    t14 = <Heading level="heading-xs"><span>{t13}:</span></Heading>;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== handlePredefinedAmountSelection || $[28] !== prefills[0]) {
    t15 = <PredefinedAmountBox cents={prefills[0]} imageSrc={images[2]} onClick={handlePredefinedAmountSelection} />;
    $[27] = handlePredefinedAmountSelection;
    $[28] = prefills[0];
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== handlePredefinedAmountSelection || $[31] !== prefills[1]) {
    t16 = <PredefinedAmountBox cents={prefills[1]} imageSrc={images[1]} onClick={handlePredefinedAmountSelection} />;
    $[30] = handlePredefinedAmountSelection;
    $[31] = prefills[1];
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== handlePredefinedAmountSelection || $[34] !== prefills[2]) {
    t17 = <PredefinedAmountBox cents={prefills[2]} imageSrc={images[0]} onClick={handlePredefinedAmountSelection} />;
    $[33] = handlePredefinedAmountSelection;
    $[34] = prefills[2];
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t15 || $[37] !== t16 || $[38] !== t17) {
    t18 = <LayoutCluster space="100" className={styles.amountBoxesContainer}>{t15}{t16}{t17}</LayoutCluster>;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t) {
    t19 = t("amount_label");
    $[40] = t;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  const t20 = value / 100;
  const t21 = amountCents / 100;
  let t22;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Icon name="euroSign" title="Euro" color="fg-content-primary" />;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== handleEnter || $[44] !== locale || $[45] !== onValueChanged || $[46] !== t19 || $[47] !== t20 || $[48] !== t21) {
    t23 = <AmountField label={t19} name="codonation_cents" id="codonation-input" value={t20} onChange={onValueChanged} onBlur={onValueChanged} onKeyDown={handleEnter} inputSize="large" step="1" max={t21} hideSteps={true} locale={locale}>{t22}</AmountField>;
    $[43] = handleEnter;
    $[44] = locale;
    $[45] = onValueChanged;
    $[46] = t19;
    $[47] = t20;
    $[48] = t21;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = classNames(styles.codonateButton, "iframeButton");
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== t) {
    t25 = t("change_amount_button");
    $[51] = t;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== storeAndClose || $[54] !== t25) {
    t26 = <Button onClick={storeAndClose} className={t24} type="button">{t25}</Button>;
    $[53] = storeAndClose;
    $[54] = t25;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== t23 || $[57] !== t26) {
    t27 = <div className={styles.codonationInputWrapper}>{t23}{t26}</div>;
    $[56] = t23;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== value) {
    t28 = <ZeroCodonationMessage value={value} />;
    $[59] = value;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== carrierName || $[62] !== carrierProfilePicture || $[63] !== t || $[64] !== wording) {
    t29 = carrierName && <div className={styles.organisation}>{carrierProfilePicture && <Image src={carrierProfilePicture} alt="" width={100} height={100} style={{
        height: "auto"
      }} />}<p className={styles.organisationTeaser}>{t(`${toLowerCase(wording)}.trust_teaser`, {
          carrier_name: carrierName
        })}</p></div>;
    $[61] = carrierName;
    $[62] = carrierProfilePicture;
    $[63] = t;
    $[64] = wording;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== t10 || $[67] !== t14 || $[68] !== t18 || $[69] !== t27 || $[70] !== t28 || $[71] !== t29) {
    t30 = <div className={styles.codonationModalContent}><LayoutStack space="400">{t10}{t14}{t18}{t27}{t28}{t29}</LayoutStack></div>;
    $[66] = t10;
    $[67] = t14;
    $[68] = t18;
    $[69] = t27;
    $[70] = t28;
    $[71] = t29;
    $[72] = t30;
  } else {
    t30 = $[72];
  }
  return t30;
}
function PredefinedAmountBox(t0) {
  const $ = _c(20);
  const {
    cents,
    imageSrc,
    onClick: onClick_
  } = t0;
  const locale = useLocale();
  const t = useTranslations("nextjs.donate.codonation_selection.codonation_modal");
  let t1;
  if ($[0] !== cents || $[1] !== onClick_) {
    t1 = () => onClick_(cents);
    $[0] = cents;
    $[1] = onClick_;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onClick = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getButtonStyles({
      kind: "primary",
      size: "default"
    });
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const buttonStyles = t2;
  let t3;
  if ($[4] !== cents || $[5] !== locale) {
    t3 = formatCents(locale, cents);
    $[4] = cents;
    $[5] = locale;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== imageSrc) {
    t4 = <div className={styles.predefinedImageWrapper}><Image src={imageSrc} alt="" fill={true} className={styles.predefinedImage} /></div>;
    $[7] = imageSrc;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("choose_button");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <span className={buttonStyles}>{t5}</span>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t6) {
    t7 = <LayoutStack space="300" className={styles.predefinedAmountContainer}>{t3}{t4}{t6}</LayoutStack>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== onClick || $[18] !== t7) {
    t8 = <button type="button" className={styles.predefinedAmountBox} onClick={onClick}>{t7}</button>;
    $[17] = onClick;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
function CodonationSelection() {
  const $ = _c(25);
  const t = useTranslations("nextjs");
  const {
    wording,
    receiverName
  } = useDonationFormConfig();
  const {
    openModal,
    hideModal,
    modalShown
  } = useCodonationModalState();
  let t0;
  if ($[0] !== t) {
    t0 = t("donate.codonation_selection.codonation_modal.open_button");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== openModal || $[3] !== t0) {
    t1 = <LinkButton className={styles.openCodonationModalButton} onClick={openModal} type="button">{t0}</LinkButton>;
    $[2] = openModal;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== wording) {
    t2 = toLowerCase(wording);
    $[5] = wording;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = `donate.codonation_selection.codonation_modal.${t2}.headline`;
  let t4;
  if ($[7] !== receiverName || $[8] !== t || $[9] !== t3) {
    t4 = t(t3, {
      receiver_name: receiverName
    });
    $[7] = receiverName;
    $[8] = t;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t) {
    t5 = t("core.close");
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      modal: styles.modal,
      modalInner: styles.modalInner
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== hideModal) {
    t7 = <ModalContent handleClose={hideModal} />;
    $[14] = hideModal;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== hideModal || $[17] !== modalShown || $[18] !== t4 || $[19] !== t5 || $[20] !== t7) {
    t8 = <ZeroCodonationMessageContextProvider><LayoutModal id="codonation-selection-modal" onClose={hideModal} isOpen={modalShown} onOpen={postMessageScrollTop} title={t4} closeText={t5} hideActions={true} classNames={t6}>{t7}</LayoutModal></ZeroCodonationMessageContextProvider>;
    $[16] = hideModal;
    $[17] = modalShown;
    $[18] = t4;
    $[19] = t5;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t1 || $[23] !== t8) {
    t9 = <div className={styles.codonationSelectionContainer}>{t1}{t8}</div>;
    $[22] = t1;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
export default CodonationSelection;